import React from 'react';
import { generateId } from '../../../utils/generateId';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  DragIndicator as DragIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ContentBlock, BlockType } from '../../../types/blocks';
import AddBlockToolbar from './AddBlockToolbar';
import TextBlockEditor from './TextBlockEditor';
import HeadingBlockEditor from './HeadingBlockEditor';
import CalloutBlockEditor from './CalloutBlockEditor';
import CodeBlockEditor from './CodeBlockEditor';
import ImageBlockEditor from './ImageBlockEditor';
import VideoBlockEditor from './VideoBlockEditor';
import ChecklistBlockEditor from './ChecklistBlockEditor';
import ImageGridBlockEditor from './ImageGridBlockEditor';
import DividerBlock from './DividerBlock';

interface BlockEditorProps {
  blocks: ContentBlock[];
  onChange: (blocks: ContentBlock[]) => void;
}

const defaultMeta: Record<BlockType, Record<string, any>> = {
  text: {},
  heading: { level: 2 },
  callout: { variant: 'info' },
  code: { language: 'plaintext' },
  image: {},
  'image-grid': { columns: 3, images: [] },
  video: {},
  checklist: { items: [] },
  divider: {},
};

function createBlock(type: BlockType, order: number): ContentBlock {
  return {
    id: generateId(),
    type,
    content: '',
    meta: { ...defaultMeta[type] },
    order,
  };
}

interface SortableBlockProps {
  block: ContentBlock;
  onUpdate: (block: ContentBlock) => void;
  onDelete: (id: string) => void;
}

function SortableBlock({ block, onUpdate, onDelete }: SortableBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const renderEditor = () => {
    switch (block.type) {
      case 'text': return <TextBlockEditor block={block} onChange={onUpdate} />;
      case 'heading': return <HeadingBlockEditor block={block} onChange={onUpdate} />;
      case 'callout': return <CalloutBlockEditor block={block} onChange={onUpdate} />;
      case 'code': return <CodeBlockEditor block={block} onChange={onUpdate} />;
      case 'image': return <ImageBlockEditor block={block} onChange={onUpdate} />;
      case 'image-grid': return <ImageGridBlockEditor block={block} onChange={onUpdate} />;
      case 'video': return <VideoBlockEditor block={block} onChange={onUpdate} />;
      case 'checklist': return <ChecklistBlockEditor block={block} onChange={onUpdate} />;
      case 'divider': return <DividerBlock />;
      default: return null;
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        py: 0.5,
        '&:hover .block-controls': { opacity: 1 },
      }}
    >
      <Box
        className="block-controls"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          opacity: 0,
          transition: 'opacity 0.15s',
          pt: 0.5,
          flexShrink: 0,
        }}
      >
        <Tooltip title="Drag to reorder" placement="left">
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              color: 'text.disabled',
              display: 'flex',
              '&:hover': { color: 'text.secondary' },
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <DragIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {renderEditor()}
      </Box>
      <Box
        className="block-controls"
        sx={{
          opacity: 0,
          transition: 'opacity 0.15s',
          pt: 0.5,
          flexShrink: 0,
        }}
      >
        <Tooltip title="Delete block" placement="right">
          <IconButton
            size="small"
            onClick={() => onDelete(block.id)}
            sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const BlockEditor: React.FC<BlockEditorProps> = ({ blocks, onChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const addBlock = (type: BlockType, afterIndex: number = blocks.length - 1) => {
    const newBlock = createBlock(type, afterIndex + 1);
    const updated = [
      ...blocks.slice(0, afterIndex + 1),
      newBlock,
      ...blocks.slice(afterIndex + 1),
    ].map((b, i) => ({ ...b, order: i }));
    onChange(updated);
  };

  const updateBlock = (updatedBlock: ContentBlock) => {
    onChange(blocks.map((b) => (b.id === updatedBlock.id ? updatedBlock : b)));
  };

  const deleteBlock = (blockId: string) => {
    onChange(blocks.filter((b) => b.id !== blockId).map((b, i) => ({ ...b, order: i })));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    const reordered = arrayMove(blocks, oldIndex, newIndex).map((b, i) => ({ ...b, order: i }));
    onChange(reordered);
  };

  return (
    <Box>
      {blocks.length === 0 && (
        <AddBlockToolbar onAdd={(type) => addBlock(type, -1)} />
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          {blocks.map((block, index) => (
            <React.Fragment key={block.id}>
              <SortableBlock
                block={block}
                onUpdate={updateBlock}
                onDelete={deleteBlock}
              />
              <AddBlockToolbar onAdd={(type) => addBlock(type, index)} />
            </React.Fragment>
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default BlockEditor;
